import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowUpRight, ShieldCheck, BadgeCheck, Clock, Leaf } from "lucide-react";
import { BUSINESS, IMAGES } from "@/data/content";
import { Reveal } from "@/components/motion/Reveal";

const BADGES = [
  { icon: ShieldCheck, label: "TSBC Licensed" },
  { icon: Leaf, label: "CleanBC Approved" },
  { icon: BadgeCheck, label: "Fully Insured" },
  { icon: Clock, label: "24/7 Response" },
];

export default function Hero() {
  // Parallax on hero image — subtle, ~10% translate range.
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.15, 0.55]);

  return (
    <section
      id="top"
      ref={heroRef}
      data-testid="hero-section"
      className="relative pt-36 md:pt-44 pb-16 md:pb-24 overflow-hidden grain"
    >
      {/* Ambient red glow */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -top-40 -left-40 w-[520px] h-[520px] bg-[#E60000]/10 blur-[120px] rounded-full"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.6, ease: "easeOut" }}
      />

      {/* Fine editorial grid — appears with delay */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.06 }}
        transition={{ delay: 0.6, duration: 1 }}
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "80px 100%",
        }}
      />

      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            {/* Eyebrow */}
            <motion.div
              className="flex items-center gap-3 text-xs tracking-[0.24em] uppercase text-white/60 mb-6"
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.05, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <motion.span
                className="inline-block h-px bg-[#E60000]"
                initial={{ width: 0 }}
                animate={{ width: 32 }}
                transition={{ delay: 0.15, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              />
              Serving Vancouver &amp; the Lower Mainland
            </motion.div>

            {/* Signature H1 — masked line-by-line reveal */}
            <h1
              data-testid="hero-headline"
              className="font-display text-5xl sm:text-6xl lg:text-[88px] leading-[0.95] tracking-tighter font-black"
            >
              <span className="block">
                <Reveal delay={0.15}>Power done&nbsp;</Reveal>
                <Reveal delay={0.28} className="text-[#E60000]">right</Reveal>
                <Reveal delay={0.28}>.</Reveal>
              </span>
              <span className="block text-white/40">
                <Reveal delay={0.45}>On the first visit.</Reveal>
              </span>
            </h1>

            <motion.p
              className="mt-6 max-w-xl text-base md:text-lg text-white/70 leading-relaxed"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              ElectroMax delivers premium residential and commercial electrical
              work across Metro Vancouver — EV chargers, panel upgrades,
              lighting, and 24/7 emergency service, backed by code-compliant
              craftsmanship and transparent flat-rate pricing.
            </motion.p>

            <motion.div
              className="mt-8 flex flex-wrap gap-3"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.82, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <a
                href="#contact"
                data-testid="hero-quote-cta"
                className="group inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold tracking-wide px-7 py-4 text-sm transition-colors"
              >
                Get a Free Quote
                <ArrowUpRight
                  className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  strokeWidth={2.5}
                />
              </a>
              <a
                href={BUSINESS.phoneHref}
                data-testid="hero-call-cta"
                className="inline-flex items-center gap-2 bg-transparent text-white border border-[#262626] hover:border-white transition-colors font-bold tracking-wide px-7 py-4 text-sm"
              >
                <Phone className="w-4 h-4" strokeWidth={2.5} />
                {BUSINESS.phone}
              </a>
            </motion.div>

            <motion.div
              className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#262626] border border-[#262626] max-w-2xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.0, duration: 0.8 }}
            >
              {BADGES.map(({ icon: Icon, label }, i) => (
                <motion.div
                  key={label}
                  className="bg-[#0A0A0A] px-4 py-4 flex items-center gap-2.5"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.05 + i * 0.08, duration: 0.5, ease: "easeOut" }}
                >
                  <Icon className="w-4 h-4 text-[#E60000]" strokeWidth={2} />
                  <span className="text-[11px] sm:text-xs tracking-wider uppercase text-white/80 font-semibold">
                    {label}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Hero image with parallax + clipped frame */}
          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="relative border border-[#262626] overflow-hidden">
              <motion.div style={{ y: imgY, scale: imgScale }} className="will-change-transform">
                <img
                  src={IMAGES.hero}
                  alt="Licensed ElectroMax journeyman electrician working on a 200-amp service panel in Vancouver"
                  className="w-full h-[420px] md:h-[540px] object-cover grayscale-[15%]"
                  loading="eager"
                  fetchPriority="high"
                />
              </motion.div>
              <motion.div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/20 to-transparent pointer-events-none"
                style={{ opacity: overlayOpacity }}
              />
              {/* Spotlight ring — clipped highlight */}
              <div
                aria-hidden
                className="pointer-events-none absolute -inset-1"
                style={{
                  background:
                    "radial-gradient(600px circle at 30% 40%, rgba(230,0,0,0.18), transparent 60%)",
                  mixBlendMode: "screen",
                }}
              />
              <motion.div
                className="absolute left-0 bottom-0 p-5 md:p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.8, ease: "easeOut" }}
              >
                <div className="text-[10px] tracking-[0.24em] uppercase text-white/60 mb-1">
                  On the job · Vancouver
                </div>
                <div className="font-display text-xl md:text-2xl tracking-tight">
                  200A service upgrade
                </div>
              </motion.div>
              <motion.div
                className="absolute -top-3 -left-3 hidden md:block bg-[#E60000] text-white text-[10px] tracking-[0.24em] uppercase font-bold px-3 py-2"
                initial={{ opacity: 0, x: -20, rotate: -6 }}
                animate={{ opacity: 1, x: 0, rotate: 0 }}
                transition={{ delay: 0.75, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              >
                Est. {new Date().getFullYear() - 12}
              </motion.div>
            </div>

            <motion.div
              className="mt-6 grid grid-cols-3 gap-px bg-[#262626] border border-[#262626] text-center"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.7 }}
            >
              <div className="bg-[#0A0A0A] py-4">
                <div className="font-display text-2xl font-black">1,200+</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-white/60 mt-1">
                  Jobs Delivered
                </div>
              </div>
              <div className="bg-[#0A0A0A] py-4">
                <div className="font-display text-2xl font-black">5.0★</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-white/60 mt-1">
                  Google Rating
                </div>
              </div>
              <div className="bg-[#0A0A0A] py-4">
                <div className="font-display text-2xl font-black">24/7</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-white/60 mt-1">
                  Emergency
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
